import React, { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { MapPin, Check, Search, X } from 'lucide-react';

const POPULAR_CITIES = [
  'New York, NY',
  'Los Angeles, CA',
  'Chicago, IL',
  'Houston, TX',
  'Phoenix, AZ',
  'Philadelphia, PA',
  'San Antonio, TX',
  'San Diego, CA',
  'Dallas, TX',
  'San Jose, CA',
  'Austin, TX',
  'Jacksonville, FL',
  'Fort Worth, TX',
  'Columbus, OH',
  'Charlotte, NC',
  'San Francisco, CA',
  'Indianapolis, IN',
  'Seattle, WA',
  'Denver, CO',
  'Washington, DC',
  'Boston, MA',
  'El Paso, TX',
  'Nashville, TN',
  'Detroit, MI',
  'Oklahoma City, OK',
  'Portland, OR',
  'Las Vegas, NV',
  'Memphis, TN',
  'Louisville, KY',
  'Baltimore, MD',
  'Milwaukee, WI',
  'Albuquerque, NM',
  'Tucson, AZ',
  'Fresno, CA',
  'Sacramento, CA',
  'Mesa, AZ',
  'Kansas City, MO',
  'Atlanta, GA',
  'Long Beach, CA',
  'Colorado Springs, CO',
  'Raleigh, NC',
  'Miami, FL',
  'Virginia Beach, VA',
  'Omaha, NE',
  'Oakland, CA',
  'Minneapolis, MN',
  'Tulsa, OK',
  'Arlington, TX',
  'Tampa, FL',
  'New Orleans, LA'
];

const CitySelector = ({ 
  selectedCity, 
  onCitySelect, 
  onSave, 
  onCancel, 
  onRemove,
  isEditing = false,
  loading = false,
  className = "" 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredCities, setFilteredCities] = useState(POPULAR_CITIES);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    const filtered = POPULAR_CITIES.filter(city =>
      city.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCities(filtered);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCitySelect = (city) => {
    onCitySelect(city);
    setIsOpen(false);
    setSearchTerm('');
    // Auto-save when a city is selected
    if (onSave) {
      onSave(city);
    }
    // Exit editing mode after saving
    if (onCancel) {
      onCancel();
    }
  };

  const handleSave = () => {
    if (selectedCity) {
      onSave(selectedCity);
    }
  };

  const handleCancel = () => {
    onCancel();
    setSearchTerm('');
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
  };

  if (!isEditing) {
    return (
      <div className={`group relative overflow-hidden bg-runcards-foreground/50 backdrop-blur-sm border border-runcards-border/20 rounded-lg hover:bg-runcards-foreground/70 hover:border-runcards-border/40 transition-all duration-200 ${className}`}>
        <div className="relative flex items-center justify-between p-3">
          <div className="flex items-center flex-1 min-w-0">
            <div className={`w-6 h-6 rounded-md flex items-center justify-center mr-3 transition-all duration-200 ${
              selectedCity 
                ? 'bg-runcards-accent/20' 
                : 'bg-runcards-text-secondary/20'
            }`}>
              <MapPin className={`w-3 h-3 transition-colors duration-200 ${
                selectedCity ? 'text-runcards-accent' : 'text-runcards-text-secondary'
              }`} />
            </div>
            <div className="flex-1 min-w-0">
              <span className={`text-sm font-medium transition-colors duration-200 truncate ${
                selectedCity ? 'text-runcards-text-primary' : 'text-runcards-text-secondary'
              }`}>
                {selectedCity || 'No location set'}
              </span>
            </div>
          </div>
          
          {selectedCity && onRemove && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemove}
              className="text-red-400 border-red-400/30 hover:bg-red-500/10 hover:text-red-300 hover:border-red-400/50 transition-all duration-200 touch-manipulation h-6 w-6 p-0 rounded-md"
            >
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`} ref={dropdownRef}>
      <div className="relative">
        <div className="relative">
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-runcards-accent w-4 h-4 transition-colors duration-200 group-focus-within:text-runcards-text-primary" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search for your city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsOpen(true)}
              className="pl-10 pr-4 h-10 bg-runcards-background/50 border border-runcards-border/20 text-runcards-text-primary placeholder:text-runcards-text-secondary touch-manipulation rounded-lg focus:border-runcards-accent/50 focus:ring-1 focus:ring-runcards-accent/20 transition-all duration-200 text-sm"
            />
          </div>
        </div>
        
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-runcards-foreground backdrop-blur-xl border border-runcards-border/20 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto city-dropdown">
            <div className="p-1">
              {filteredCities.length > 0 ? (
                filteredCities.map((city) => (
                  <button
                    key={city}
                    onClick={() => handleCitySelect(city)}
                    className="w-full px-3 py-2 text-left text-runcards-text-primary hover:bg-runcards-background/50 hover:text-runcards-text-primary transition-all duration-200 flex items-center justify-between touch-manipulation group rounded-md text-sm"
                  >
                    <span className="flex items-center">
                      <div className="w-6 h-6 bg-runcards-accent/20 rounded-md flex items-center justify-center mr-3 group-hover:bg-runcards-accent/30 transition-colors duration-200">
                        <MapPin className="w-3 h-3 text-runcards-accent" />
                      </div>
                      <span className="font-medium">{city}</span>
                    </span>
                    {selectedCity === city && (
                      <div className="w-5 h-5 bg-runcards-accent rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-runcards-text-primary" />
                      </div>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-runcards-text-secondary text-center">
                  <MapPin className="w-6 h-6 mx-auto mb-2 text-runcards-text-secondary" />
                  <p className="font-medium text-sm mb-1">No cities found</p>
                  <p className="text-xs">Try a different search term</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {selectedCity && (
        <div className="bg-runcards-accent/10 border border-runcards-accent/30 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-runcards-accent/20 rounded-md flex items-center justify-center mr-3">
                <MapPin className="w-3 h-3 text-runcards-accent" />
              </div>
              <div>
                <span className="text-runcards-text-primary font-medium text-sm block">{selectedCity}</span>
                <span className="text-runcards-text-secondary text-xs">Selected location</span>
              </div>
            </div>
            <div className="w-5 h-5 bg-runcards-accent/20 rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-runcards-accent" />
            </div>
          </div>
        </div>
      )}

      {/* Only show action buttons if not auto-saving */}
      {!isEditing && (
        <div className="flex gap-2">
          {selectedCity && onRemove && (
            <Button
              onClick={handleRemove}
              disabled={loading}
              variant="outline"
              className="text-red-400 border-red-400/40 hover:bg-red-500/10 hover:text-red-300 hover:border-red-400/60 touch-manipulation h-8 px-3 text-sm font-medium rounded-md transition-all duration-200"
            >
              <X className="w-3 h-3 mr-1" />
              Remove
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CitySelector;
